import { Padding } from "../utils/Padding";
import { RectSize } from "../utils/RectSize";

/**
 * Geometry Mask that adjusts its position/size/rotation to container position/size/rotation,
 * or to specific content within or without container
 * 
 * Exec Order:
    this.contentMask = new SmartMask(100, 200);
    // this.contentMask.DEBUG = false;
    this.contentMask.Init(this, new Phaser.Geom.Rectangle(-300, -50, 600, 100));
    this.contentMask.SetContent(this.content);
 * 
 * NOTES:
 * https://rexrainbow.github.io/phaser3-rex-notes/docs/site/mask/
 * 
 * NOTES:
 * Don't put game object and its mask into a container together. See this testing, enable line 22-24.
 * Bitmap Mask is WebGL only.
 * Can combine Geometry Masks and Blend Modes on the same Game Object, but Bitmap Masks can't.
 * 
 * GeometryMask:
 * A Geometry Mask can be applied to a Game Object to hide any pixels of it which don't intersect
 * a visible pixel from the geometry mask. The mask is essentially a clipping path which can only
 * make a masked pixel fully visible or fully invisible without changing its alpha (opacity).
 * 
 * A Geometry Mask uses a Graphics Game Object to determine which pixels of the masked Game Object(s)
 * should be clipped. For any given point of a masked Game Object's texture, the pixel will only be displayed
 * if the Graphics Game Object of the Geometry Mask has a visible pixel at the same position. The color and
 * alpha of the pixel from the Geometry Mask do not matter.
 * 
 * The Geometry Mask's location matches the location of its Graphics object, not the location of the masked objects.
 * Moving or transforming the underlying Graphics object will change the mask (and affect the visibility
 * of any masked objects), whereas moving or transforming a masked object will not affect the mask.
 * You can think of the Geometry Mask (or rather, of its Graphics object) as an invisible curtain placed
 * in front of all masked objects which has its own visual properties and, naturally, respects the camera's
 * visual properties, but isn't affected by and doesn't follow the masked objects by itself.
 * 
 * TODO: can simplify container trasnform calc using phaser func?
 * Q: How can i get the world space position of an object inside a container? 
 *    camera.getWorldPoint doesnt do the trick entirely
 * A: const { tx, ty } = gameObject.getWorldTransformMatrix();
 */
export class SmartMask
{
    /**
     * 
     */
    public mask: Phaser.Display.Masks.GeometryMask;

    /**
     * container into which to put the mask
     */
    public parentContainer: Phaser.GameObjects.Container;

    /**
     * Rectangle used to create + draw maskGraphics for mask 
     * and maskGraphicsDebug for debug view of said mask
     */
    public maskRect: Phaser.Geom.Rectangle;
    
    /**
     * Graphics that sources the mask
     * - Graphics can be put inside container tree, but the geometryMask is global.
     * - Transforms applied to graphics GO will affect visual of mask
     * - Need to reverse-transform graphics object (using it's parent transforms) 
     *   to correctly display the mask in global scope.
     */
    public maskGraphics: Phaser.GameObjects.Graphics;
    
    /**
     * Mirror of maskGraphics, used for debug
     */
    public maskGraphicsDebug: Phaser.GameObjects.Graphics;

    /**
     * debug mask shape? 
     * NOTE: mask is global, so to debug the shape, we use local graphic, which mirrors the mask locally.
     */
    public DEBUG: boolean = false;

    private _x: number;
    private _y: number;

    /**
     * Extra extrude for the mask
     */
    protected extrude: Padding;


    /**
     * NOTE: it is necessary to call @see UpdateTransforms after setting position, however things are not yet ready to update at this point, and we will rely on pending parent's OnResize to update mask transforms.
     * 
     * @param x 
     * @param y 
     */
    public constructor(x: number = 0, y: number = 0)
    {
        this._x = x;
        this._y = y;
    }

    public get x(): number {
        return this._x;
    }

    public set x(value: number) {
        this._x = value;
    }

    public get y(): number {
        return this._y;
    }

    public set y(value: number) {
        this._y = value;
    }

    public get width(): number {
        return this.maskRect.width;
    }

    public get height(): number {
        return this.maskRect.height;
    }

    public SetPosition(x, y): void
    {
        this._x = x;
        this._y = y;
        this.UpdateTransforms();
    }

    /**
     * 
     * @param parentContainer 
     * @param maskGraphics 
     * @param graphicsMirrorDebug mirror graphics mask object used for mask debug
     */
    public Init(
        parentContainer: Phaser.GameObjects.Container, 
        maskRect: Phaser.Geom.Rectangle = undefined,
        extrude: Padding = undefined
    ): void
    {
        this.parentContainer = parentContainer;
        this.maskRect = maskRect == undefined ? new Phaser.Geom.Rectangle() : maskRect;
        this.extrude = extrude == undefined ? new Padding(0) : extrude;

        // smart mask graphic is transformed to follow position/rotation/scale of container
        // SmartMask (geometryMask) follows transformations of this graphic
        this.maskGraphics = parentContainer.scene.make.graphics({});
        
        if (this.DEBUG) {
            this.maskGraphicsDebug = parentContainer.scene.make.graphics({});
            this.parentContainer.add(this.maskGraphicsDebug);
        }
        
        this.mask = this.maskGraphics.createGeometryMask();

        // is rect mask
        // TODO: allow other types of masks by implementing maskGraphics direct set from outside.
        //       or just expose this.maskGraphics and redraw outside!
        this.UpdateGraphics();
        this.UpdateTransforms();

        // // DEBUG: animate mask
        // this.parentContainer.scene.tweens.add({
        //     targets: [this.maskGraphics, this.maskGraphicsDebug],
        //     angle: 360,
        //     duration: 80000,
        //     repeat: -1,
        // });
    }

    /**
     * Set content to be masked by mask
     * @param content 
     */
    public SetContent(
        content: 
            Phaser.GameObjects.Container | 
            Phaser.GameObjects.Image | 
            Phaser.GameObjects.Graphics
    ): void {
        // this.maskContent = content;
        content.setMask(this.mask);
    }

    public SetMaskRect(rect: Phaser.Geom.Rectangle): void
    {
        this.maskRect = rect;
        this.UpdateGraphics();
    }

    public SetMaskRectSize(width: number, height?: number): void;
    public SetMaskRectSize(size: RectSize): void;
    public SetMaskRectSize(a: any, b: any = undefined): void
    {
        // console.log("SetMaskRectSize",a,b);

        // its (width, height) overload
        if (b != undefined) {
            this.maskRect.setSize(a, b);
        }
        // else its RectSize overload
        else {
            this.maskRect.setSize(a.width, a.height);
        }
        this.UpdateGraphics();
    }
    

    /**
     * Update by redrawing mask graphics (and mask debug graphics if applicable),
     * from maskRect change
     */
    public UpdateGraphics(): void
    {
        // console.log("SmartMask::RedrawGraphics",this.maskRect);

        this.maskGraphics.clear();
        this.maskGraphics.fillStyle(0xff0000, 0.6);
        this.maskGraphics.fillRect(
            -this.extrude.left + this.maskRect.x, 
            -this.extrude.top + this.maskRect.y, 
            this.extrude.left + this.maskRect.width + this.extrude.right, 
            this.extrude.top + this.maskRect.height + this.extrude.bottom
        );
    
        if (this.DEBUG) {
            this.maskGraphicsDebug.clear();
            this.maskGraphicsDebug.fillStyle(0xff0000, 0.5);
            this.maskGraphicsDebug.fillRect(
                -this.extrude.left + this.maskRect.x, 
                -this.extrude.top + this.maskRect.y, 
                this.extrude.left + this.maskRect.width + this.extrude.right, 
                this.extrude.top + this.maskRect.height + this.extrude.bottom
            );
        }
    }

    /**
     * Transform maskGraphics to properly respect parent container(s) transform
     * of displayList tree which it is part of.
     */
    public UpdateTransforms(): void
    {
        const tm = this.parentContainer.getWorldTransformMatrix();
        // console.log(tm.tx, tm.ty);

        // smart mask or 'masked smart graphics'?
        this.maskGraphics.setScale(tm.a, tm.d);
        this.maskGraphics.setPosition(tm.tx + this._x, tm.ty + this._y);

        if (this.DEBUG) {
            this.maskGraphicsDebug.setPosition(this._x, this._y);
        }
    }
}